import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import DurationInput from 'modules/core/components/DurationInput'
import FormRow from 'modules/core/components/FormRow'
import TimePicker from 'modules/core/components/Inputs/TimePicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import DateRangePicker from 'modules/timeoff/components/DateRangePicker'
import { useMinimumLeaveDate } from 'modules/timeoff/components/LeaveContent/OtherLeavesForm/DayTypeForm/DayTypes/useMinimumLeaveDate'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'

export interface Props {
  form: LeaveForm
  holidays: DaysTree
  blockedPeriods: DaysTree
  existingLeaves: DaysTree
  readOnly?: boolean
}

const getFirstError = (field: Field) => {
  return field.errors ? field.errors[0] : ''
}

const Hours = ({ form, holidays, blockedPeriods, readOnly, existingLeaves }: Props) => {
  const { access } = useCurrentSession()
  const { startsOn } = useContractsVersion()
  const minDate = useMinimumLeaveDate(startsOn)
  const hideRequestInMinutes = hasFeature('timeoff_hide_requests_in_minutes')

  // TODO: 🔥👇
  useUnsafeEffect(() => {
    return reaction(
      () => form.get('start_on').value,
      (startOn: string) => form.get('finish_on').set(startOn)
    )
  }, [form])

  // decimal hours like: 1.5, 2.5 will return 90mins, 150mins
  const decimalHoursToFullMinutes = (decimalHours: number) => {
    return Math.round(decimalHours * 60.0)
  }

  // minutes like: 90mins, 150mins will return 1.5hours, 2.5hours
  const minutesToDecimalHours = (minutes: number): number => {
    return Number((minutes / 60.0).toFixed(2))
  }

  const startTime = form.get('start_time')

  const handleBlur = () => {
    if (form.startTimeHasValue) return

    return startTime.setErrors([i18n.t('common.errors.required')])
  }

  const handleFocus = () => {
    return startTime.setErrors(null)
  }

  return (
    <>
      <FormRow>
        <DateRangePicker
          startDate={form.get('start_on')}
          endDate={form.get('finish_on')}
          readonly={!!readOnly}
          minDate={minDate.current}
          holidays={holidays}
          blockedPeriods={blockedPeriods}
          existingLeaves={existingLeaves}
          withNull={form.endDateOptional}
          isHourlyRequest
        />
      </FormRow>
      <Box flexDirection="row" justifyContent="spaceBetween" gap="s24" paddingBottom="s24">
        <Box style={{ width: '50%' }}>
          <TimePicker
            label={i18n.t('time_off.starts')}
            field={startTime}
            readonly={readOnly}
            isTimeFormat24H={access.timeFormat === 24}
            mandatory
            error={getFirstError(startTime)}
            onBlur={handleBlur}
            onFocus={handleFocus}
          />
        </Box>
        <Box style={{ width: '50%' }}>
          <DurationInput
            readonly={readOnly}
            label={i18n.t('time_off.amount_hours_off_per_day')}
            hideMinute={hideRequestInMinutes}
            minutes={decimalHoursToFullMinutes(form.get('hours_amount_in_cents').value)}
            onChange={(value) =>
              form.get('hours_amount_in_cents').set(String(minutesToDecimalHours(value)))
            }
          />
        </Box>
      </Box>
    </>
  )
}

export default observer(Hours)
